import React, { useContext, useEffect, useState } from 'react';
import './VehiclePage.scss';
//@ts-ignore
import DateInput from '../DateInput/DateInput';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import Tooltip from '@mui/material/Tooltip';
import {
  ManualQuerySelections,
  VehicleState,
  VehicleType
} from '../CommonTypes';
import RegistrationInput from '../RegistrationInput';
import { getFormattedDate } from '../HelperMethods';
import VehicleDetailsManual from './VehicleDetailsManual';
import ModelTrimInput from './ModelTrimInput';
import { ButtonCommand } from '../Button/ButtonCommand';
import Checkbox from '../../common/Checkbox/Checkbox';
import { ButtonConfirm } from '../Button/ButtonConfirm';
import {
  formatNumber,
  getCurrencySymbol,
  getDateStringByBrowserLanguage
} from '../Formatter';
import PriceInput from '../PriceInput/PriceInput';
import NumberInput from '../NumberInput/NumberInput';
import { CommonValidation } from '../CommonValidation/commonValidation';
import LoadingContainer from '../LoadingContainer/LoadingContainer';
import TextInput from '../TextInput/TextInput';
import ClientDateHandler from '../ClientDateHandler/ClientDateHandler';
import { useTranslation } from 'react-i18next';
import { CountryIsoCodes } from '../Constants';
import { SiteContext } from '../../../context/SiteContext';
import { Journey } from '../../../consts/Common';

export type Ref = HTMLButtonElement;

export type VehicleErrors = {
  currentMileage?: string;
  vehicleDeliveryDate?: string;
  vehiclePurchasePrice?: string;
  policyStartDate?: string;
  vehicleGapIntroductionDate?: string;
  salesId?: string;
  modelTrim?: string;
  dealerReference?: string;
};

type VehiclePageProps = {
  initialVehicleInfo: VehicleState;
  policyStartDate?: Date;
  validate: boolean;
  update: boolean;
  showEditVrn: boolean;
  showModelTrim: boolean;
  showVehicleDeliveryDate: boolean;
  showPolicyStartDate: boolean;
  showGapIntroductionDate: boolean;
  showSalesId: boolean;
  showDealerReference: boolean;
  vehiclePurchasePriceLabel: string;
  showFieldsOnDataError: boolean;
  token: string;
  isPolicyStartDateEnabled: boolean;
  showVin: boolean;
  showIsDeliveryDateEstimated: boolean;
  onValidated: (isValid: boolean) => void;
  onUpdateVehicleState: (vehicleInfo: VehicleState) => void;
  onUpdateRegistrationNumber: (registrationNumber: string) => void;
  onUpdatePolicyStartDate?: (policyStartDate: Date) => void;
  onUpdateIdentificationNumber?: (identificationNumber: string) => void;
  isReadOnly: boolean;
  futureDateAllowed: boolean;
  showNeedHelpTooltipDeliveryDate: boolean;
  showVehiclePurchasePrice: boolean;
  upsellAnnualMileageText?: string;
  exceedsUpsellMileageLimit?: boolean;
  setExceedsUpsellMileageLimit?: (exceedsUpsellMileageLimit: boolean) => void;
  showEditVehicle: boolean;
  quoteUpsell?: boolean;
  showMileage: boolean;
  isPurchasePriceEditable: boolean;
  isMileageEditable: boolean;
  isSalesIdMandatory: boolean;
  isVehicleDeliveryEditable: boolean;
  culture: string;
  externalReferenceLabelText: string;
  ignoreVinFromManualDetails?: boolean;
};

const VehiclePage = ({
  initialVehicleInfo,
  policyStartDate,
  validate,
  update,
  showEditVrn,
  showModelTrim,
  showVehicleDeliveryDate,
  showPolicyStartDate,
  showGapIntroductionDate,
  showSalesId,
  showDealerReference,
  showFieldsOnDataError,
  vehiclePurchasePriceLabel,
  token,
  isPolicyStartDateEnabled,
  showVin,
  showIsDeliveryDateEstimated,
  onValidated,
  onUpdateRegistrationNumber,
  onUpdateVehicleState,
  onUpdatePolicyStartDate,
  onUpdateIdentificationNumber,
  isReadOnly,
  futureDateAllowed,
  showNeedHelpTooltipDeliveryDate,
  showVehiclePurchasePrice,
  upsellAnnualMileageText,
  exceedsUpsellMileageLimit,
  setExceedsUpsellMileageLimit,
  showEditVehicle,
  quoteUpsell,
  showMileage,
  isPurchasePriceEditable,
  isMileageEditable,
  isSalesIdMandatory,
  culture,
  isVehicleDeliveryEditable,
  ignoreVinFromManualDetails,
  externalReferenceLabelText
}: VehiclePageProps): JSX.Element => {
  let policyStartDateJson: string | undefined = undefined;
  if (policyStartDate && policyStartDate !== undefined) {
    const cdh = new ClientDateHandler();
    cdh.setDateValue(false, new Date(policyStartDate));
    policyStartDateJson = cdh.getJSON();
  }
  const { site } = useContext(SiteContext);
  const [errors, setErrors] = useState<VehicleErrors>({});
  const [showModalDialog, setShowModalDialog] = useState(false);
  const [showModelSelect, setShowModelSelect] = useState(false);
  const [showModalDialogVin, setShowModalDialogVin] = useState(false);
  const [vehicle, setVehicle] = useState(initialVehicleInfo);
  const currencySymbol = getCurrencySymbol();
  const germanWebsite =
    site.countryCode === CountryIsoCodes.DE ||
    site.countryCode === CountryIsoCodes.AT;
  const isRequiredText =
    site.countryCode === CountryIsoCodes.DE ||
    site.countryCode === CountryIsoCodes.AT
      ? 'erforderlich'
      : 'is required';

  useEffect(() => {
    setVehicle(initialVehicleInfo);
    setUpdatedVrn(initialVehicleInfo.vehicleDetails.vrn);
    setUpdatedVin(initialVehicleInfo.vehicleDetails.vin || '');
  }, [initialVehicleInfo]);

  const {
    vehicleDetails,
    currentMileage,
    vehiclePurchasePrice,
    vehicleDeliveryDate,
    vehicleGapIntroductionDate,
    salesId,
    dealerReference,
    deliveryDateEstimated
  } = vehicle;

  const { t } = useTranslation();

  const [updatedVrn, setUpdatedVrn] = useState<string>(vehicleDetails.vrn);
  const [updatedVin, setUpdatedVin] = useState<string>(
    vehicleDetails.vin || ''
  );
  const [vrnError, setVrnError] = useState('');
  const [vinError, setVinError] = useState('');

  const vehicleRegistrationDate = getDateStringByBrowserLanguage(
    culture,
    vehicleDetails?.registrationDate
  );
  const electricFuelTypeId = 4;
  const isElectric = vehicleDetails.fuelTypeId === electricFuelTypeId;
  const { modelType, vin, vrn } = vehicleDetails;

  const vehicleNotFound =
    vehicleDetails.registrationDate === '' ||
    vehicleDetails.registrationDate === null;

  const isEmptyVrnAndVin =
    CommonValidation.isEmpty(vin) && CommonValidation.isEmpty(vrn);

  const showUpsellAnnualMileage = quoteUpsell;

  const validateMileage = (mileage: number | null): string => {
    const minMileage = 1;
    const maxMileage = 999999;

    let errorMessage = '';

    if (CommonValidation.isNotEmpty(mileage)) {
      const enteredMileage = nullToZero(mileage);

      if (enteredMileage < minMileage) {
        errorMessage = `Current mileage should be greater than ${
          minMileage - 1
        }`;
      } else if (enteredMileage > maxMileage) {
        errorMessage = `Current mileage should not be greater than ${maxMileage}`;
      }
    }
    return errorMessage;
  };

  useEffect(() => {
    const errorMsg = validateMileage(currentMileage);
    setErrors({
      ...errors,
      currentMileage: errorMsg
    });
  }, [currentMileage]);

  const validateVehicleDeliveryDate = (
    deliveryDate: string | undefined
  ): string => {
    if (
      CommonValidation.isDateNull(deliveryDate, true) ||
      CommonValidation.isEmpty(deliveryDate)
    ) {
      return '';
    }

    const deliveryDateIsValid = isVehicleDeliveryDateValid();

    if (deliveryDateIsValid) {
      return '';
    }

    if (futureDateAllowed) {
      return errorFutureDateAllowed;
    }

    return errorInvalidDeliveryDate;
  };

  useEffect(() => {
    const errorMsg = validateVehicleDeliveryDate(vehicleDeliveryDate);
    setErrors({
      ...errors,
      vehicleDeliveryDate: errorMsg
    });
  }, [vehicleDeliveryDate]);

  const validateVehicleGapIntroductionDate = (
    gapIntroductionDate: string | undefined
  ): string => {
    let errorMessage = '';
    if (!CommonValidation.isDateNull(gapIntroductionDate, true)) {
      if (!isVehicleGapIntroductionDateValid()) {
        errorMessage = errorGapDateInvalid;
      }
    }
    return errorMessage;
  };

  useEffect(() => {
    const errorMsg = validateVehicleGapIntroductionDate(
      vehicleGapIntroductionDate
    );
    setErrors({
      ...errors,
      vehicleGapIntroductionDate: errorMsg
    });
  }, [vehicleGapIntroductionDate]);

  const nullToZero = (value: number | null): number => {
    if (typeof value === 'number') {
      return value;
    }

    return 0;
  };

  const validatePage = () => {
    if (isReadOnly && !isPurchasePriceEditable && !isMileageEditable)
      return true;
    let isValid = true;

    const error: VehicleErrors = {};

    if (CommonValidation.isNotEmpty(currentMileage)) {
      const errorMessage = validateMileage(currentMileage);
      if (CommonValidation.isNotEmpty(errorMessage)) {
        isValid = false;
        error.currentMileage = errorMessage;
      }
    } else if (showMileage) {
      isValid = false;
      error.currentMileage = errorMileageRequired;
    }

    if (showVehicleDeliveryDate === true) {
      if (
        CommonValidation.isDateNull(vehicleDeliveryDate, true) ||
        CommonValidation.isEmpty(vehicleDeliveryDate)
      ) {
        isValid = false;
        error.vehicleDeliveryDate = errorDeliveryDateRequired;
      } else if (!CommonValidation.isValidDate(vehicleDeliveryDate, true)) {
        isValid = false;
        error.vehicleDeliveryDate = errorDeliveryDateOutOfRange;
      } else {
        const errorMessage = validateVehicleDeliveryDate(vehicleDeliveryDate);
        if (CommonValidation.isNotEmpty(errorMessage)) {
          isValid = false;
          error.vehicleDeliveryDate = errorMessage;
        }
      }
    }

    if (!CommonValidation.isDateNull(vehicleGapIntroductionDate, true)) {
      if (!CommonValidation.isValidDate(vehicleGapIntroductionDate, true)) {
        isValid = false;
        error.vehicleGapIntroductionDate = errorIntroDateOutOfRange;
      } else {
        const errorMessage = validateVehicleGapIntroductionDate(
          vehicleGapIntroductionDate
        );
        if (CommonValidation.isNotEmpty(errorMessage)) {
          isValid = false;
          error.vehicleGapIntroductionDate = errorMessage;
        }
      }
    }

    if (vehiclePurchasePrice == 0 && showVehiclePurchasePrice) {
      isValid = false;
      error.vehiclePurchasePrice = `${vehiclePurchasePriceLabel} ${isRequiredText}`;
    }

    if (showSalesId && isSalesIdMandatory && (!salesId || salesId === '')) {
      isValid = false;
      error.salesId = errorEmptySalesId;
    }

    if (salesId && salesId.length > 6) {
      isValid = false;
      error.salesId = errorInvalidSalesId;
    }

    if (
      (isEmptyVrnAndVin || isSalesIdMandatory) &&
      CommonValidation.isEmpty(dealerReference) &&
      showDealerReference
    ) {
      isValid = false;
      error.dealerReference = errorDealerReferenceRequired;
    } else if (dealerReference && dealerReference.length > 10) {
      isValid = false;
      error.dealerReference = t(
        'vehicle-details.error-invalid-dealer-reference',
        {
          maxLength: '10'
        }
      );
    }

    if (CommonValidation.isEmpty(modelType)) {
      isValid = false;
      error.modelTrim = errorModelTypeRequired;
    }

    setErrors(error);

    return isValid;
  };

  const isVehicleDeliveryDateValid = (): boolean => {
    const jsonVehicleDate = CommonValidation.getJsonDate(vehicleDeliveryDate);

    if (!jsonVehicleDate) {
      return false;
    }

    const deliveryDate = new Date(jsonVehicleDate).setHours(0);

    if (CommonValidation.isEmpty(vrn) && !vehicleDetails.registrationDate) {
      console.error('Vehicle registration date is not known');
      return false;
    }

    if (!vehicleDetails.registrationDate) {
      return true;
    }

    const registrationDateSplit = vehicleDetails.registrationDate.split('/');
    const registrationDate = new Date(
      parseInt(registrationDateSplit[2]),
      parseInt(registrationDateSplit[1]) - 1,
      parseInt(registrationDateSplit[0])
    ).setHours(0);

    const today = new Date().setHours(0);
    let result = true;

    if (!futureDateAllowed) {
      result = deliveryDate >= registrationDate && deliveryDate <= today;
    } else {
      result = deliveryDate >= registrationDate;
    }

    return result;
  };

  const isVehicleGapIntroductionDateValid = (): boolean => {
    const jsonGapDate = CommonValidation.getJsonDate(
      vehicleGapIntroductionDate
    );

    let gapIntroductionDate = new Date().setHours(0, 0, 0, 0);

    if (jsonGapDate) {
      gapIntroductionDate = new Date(jsonGapDate).setHours(0, 0, 0, 0);
    }

    const today = new Date().setHours(0, 0, 0, 0);

    const isValid = gapIntroductionDate <= today;

    return isValid;
  };

  const onEditClick = () => {
    setShowModelSelect(!showModelSelect);
  };

  const onShowModal = () => {
    setShowModalDialog(true);
  };

  const onShowModalVin = () => {
    setShowModalDialogVin(true);
  };

  const onChangeVehicleReg = (e: React.FormEvent<HTMLInputElement>) => {
    setUpdatedVrn(e.currentTarget.value);
  };

  const onChangeVin = (e: React.FormEvent<HTMLInputElement>) => {
    setUpdatedVin(e.currentTarget.value);
  };

  const onDeliveryDateEstimatedChange = () => {
    const newVehicleInfo = {
      ...vehicle,
      deliveryDateEstimated: !deliveryDateEstimated
    };
    setVehicle(newVehicleInfo);
  };

  const onUpsellAnnualMileageChange = () => {
    if (setExceedsUpsellMileageLimit)
      setExceedsUpsellMileageLimit(!exceedsUpsellMileageLimit);
  };

  const validateVrn = () => {
    let isValid = true;
    if (germanWebsite && site.journeyType === Journey.Customer) {
      setVrnError('');
      setVehicle({
        ...initialVehicleInfo,
        vehicleDetails: {
          ...initialVehicleInfo.vehicleDetails,
          vrn: updatedVrn
        }
      });
      return isValid;
    }
    const vrnCharLimit = 10;

    if (CommonValidation.isEmpty(updatedVrn)) {
      setVrnError(errorVrnRequired);
      isValid = false;
    } else if (!CommonValidation.isValidLength(updatedVrn, vrnCharLimit)) {
      setVrnError(`Too many characters. Max length (${vrnCharLimit})`);
      isValid = false;
    }

    if (isValid) {
      setVrnError('');
    }

    return isValid;
  };

  const onCloseModal = () => {
    setVrnError('');
    setShowModalDialog(false);
  };

  const onConfirmModal = () => {
    if (validateVrn()) {
      onUpdateVehicleState(vehicle);
      onUpdateRegistrationNumber(updatedVrn);
      onCloseModal();
    }
  };

  const onCloseModalVin = () => {
    setVinError('');
    setShowModalDialogVin(false);
  };

  const onConfirmModalVin = () => {
    if (validateVin()) {
      if (onUpdateIdentificationNumber) {
        onUpdateIdentificationNumber(updatedVin);
      }
      onCloseModalVin();
    }
  };

  const validateVin = () => {
    let isValid = true;

    const vinCharLimit = 20;

    if (CommonValidation.isEmpty(updatedVin)) {
      setVinError(errorVinRequired);
      isValid = false;
    } else if (!CommonValidation.isValidLength(updatedVin, vinCharLimit)) {
      setVinError(`Too many characters. Max length (${vinCharLimit})`);
      isValid = false;
    }

    if (isValid) {
      setVinError('');
    }

    return isValid;
  };

  const onConfirmManualVehicleModal = (
    manualQuerySelections: ManualQuerySelections,
    enginePowerBHP?: number,
    enginePowerKWH?: number,
    driveTypeId?: number,
    vehicleVin?: string,
    model?: string,
    fuelTypeCode?: string
  ) => {
    const newVehicleDetails: VehicleType = {
      ...vehicleDetails,
      registrationDate:
        getFormattedDate(manualQuerySelections.firstRegistrationDate) ?? '',
      make: manualQuerySelections.make,
      model: manualQuerySelections.model,
      fuelType: manualQuerySelections.fuelType,
      engineSize: manualQuerySelections.engineSize,
      modelTrimId: parseInt(manualQuerySelections.vehicleModelTrimId),
      ...(ignoreVinFromManualDetails ? {} : { vin: vehicleVin }),
      fuelTypeId: parseInt(manualQuerySelections.vehicleFuelTypeId),
      fuelTypeCode: fuelTypeCode ?? '',
      enginePowerBHP: enginePowerBHP,
      enginePowerKWH: enginePowerKWH,
      driveTypeId: driveTypeId,
      errorMessage: '',
      modelType: model ?? '',
      makeId: manualQuerySelections.vehicleMakeId,
      vehicleTypeId: manualQuerySelections.vehicleTypeId,
      modelId: manualQuerySelections.vehicleModelId
    };
    const newVehicleInfo = {
      ...vehicle,
      vehicleDetails: newVehicleDetails
    };
    setVehicle(newVehicleInfo);
    onUpdateVehicleState(newVehicleInfo);
  };

  const onChangeMileage = (e: React.FormEvent<HTMLInputElement>) => {
    const value = (e.target as HTMLInputElement).value;
    if (CommonValidation.isEmpty(value) || isNaN(parseInt(value))) {
      const newVehicleInfo = {
        ...vehicle,
        currentMileage: null
      };
      setVehicle(newVehicleInfo);
    } else {
      const newVehicleInfo = {
        ...vehicle,
        currentMileage: parseInt(value)
      };
      setVehicle(newVehicleInfo);
    }
  };

  const onChangeVehiclePurchasePrice = (
    e: React.FormEvent<HTMLInputElement>
  ) => {
    const value = (e.target as HTMLInputElement).value;
    if (CommonValidation.isEmpty(value) || isNaN(parseInt(value))) {
      const newVehicleInfo = {
        ...vehicle,
        vehiclePurchasePrice: 0
      };
      setVehicle(newVehicleInfo);
    } else {
      const newVehicleInfo = {
        ...vehicle,
        vehiclePurchasePrice: parseInt(value)
      };
      setVehicle(newVehicleInfo);
    }
  };

  const onChangeVehicleDeliveryDate = (
    e: React.FormEvent<HTMLInputElement>
  ) => {
    const value = (e.target as HTMLInputElement).value;

    const newVehicleInfo = {
      ...vehicle,
      vehicleDeliveryDate: value
    };
    setVehicle(newVehicleInfo);
  };

  const onChangePolicyStartDate = (e: React.FormEvent<HTMLInputElement>) => {
    const value = (e.target as HTMLInputElement).value;
    const cdh = new ClientDateHandler();
    cdh.parseJSON(value);
    const parsedDt = cdh.getDate(false);
    if (onUpdatePolicyStartDate && parsedDt) onUpdatePolicyStartDate(parsedDt);
  };

  const onChangeVehicleGapIntroductionDate = (
    e: React.FormEvent<HTMLInputElement>
  ) => {
    const value = (e.target as HTMLInputElement).value;

    const newVehicleInfo = {
      ...vehicle,
      vehicleGapIntroductionDate: value
    };
    setVehicle(newVehicleInfo);
  };

  const onChangeSalesId = (e: React.FormEvent<HTMLInputElement>) => {
    const value = (e.target as HTMLInputElement).value;
    const newVehicleInfo = {
      ...vehicle,
      salesId: value
    };
    setVehicle(newVehicleInfo);
  };

  const onChangeDealerReference = (e: React.FormEvent<HTMLInputElement>) => {
    const value = (e.target as HTMLInputElement).value;
    const newVehicleInfo = {
      ...vehicle,
      dealerReference: value
    };
    setVehicle(newVehicleInfo);
  };

  const getToolbarClassName = () => {
    let className = 'delivery-date-control';
    if (errors.vehicleDeliveryDate) {
      className += ' tooltip-error';
    }
    return className;
  };

  const getGapIntroductionContainerClass = () => {
    let className = 'gap-introduction-date-control';
    if (errors.vehicleGapIntroductionDate) {
      className += ' tooltip-error';
    }
    return className;
  };

  const getMinDate = () => {
    return new Date();
  };

  const policyStartDtClassName = isPolicyStartDateEnabled
    ? 'input-text small'
    : 'input-text small disabled';

  useEffect(() => {
    if (update === true) {
      onUpdateVehicleState(vehicle);
    }
  }, [update]);

  useEffect(() => {
    if (validate === true) {
      const isPageValid = validatePage();
      onValidated(isPageValid);
    }
  }, [validate]);

  const onChangeModelTrim = (value: string, text: string): void => {
    const newVehicleDetails = {
      ...vehicle,
      vehicleDetails: {
        ...vehicle.vehicleDetails,
        modelTrimId: +value,
        modelType: text
      }
    };

    setVehicle(newVehicleDetails);
  };

  const deliveryDateLabelText = !quoteUpsell
    ? t('vehicle-details.delivery-date-label-text')
    : t('vehicle-details.service-date-label-text');

  const needHelpTooltipHeader = t('vehicle-details.need-help-tooltip-header');

  const needHelpTooltipGapIntroductionDate = t(
    'vehicle-details.need-help-tooltip-gap-introduction-date'
  );

  const buttonConfirmText = t('vehicle-details.button-confirm-text');

  const errorInvalidDeliveryDate = t(
    'vehicle-details.error-invalid-delivery-date'
  );
  const errorFutureDateAllowed = t('vehicle-details.error-future-date-allowed');
  const errorGapDateInvalid = t('vehicle-details.error-gap-date-invalid');
  const errorMileageRequired = t('vehicle-details.error-mileage-required');
  const errorDeliveryDateRequired = t(
    'vehicle-details.error-delivery-date-required'
  );
  const errorDeliveryDateOutOfRange = t(
    'vehicle-details.error-delivery-date-out-of-range'
  );
  const errorIntroDateOutOfRange = t(
    'vehicle-details.error-intro-date-out-of-range'
  );
  const errorEmptySalesId = t('vehicle-details.error-empty-sales-id');
  const errorInvalidSalesId = t('vehicle-details.error-invalid-sales-id');
  const errorDealerReferenceRequired = t(
    'vehicle-details.error-dealer-reference-required'
  );
  const errorModelTypeRequired = t('vehicle-details.error-model-type-required');
  const errorVrnRequired = t('vehicle-details.error-vrn-required');
  const errorVinRequired = t('vehicle-details.error-vin-required');
  const brakeHorsepowerLabel = t('vehicle-details.brake-horsepower-label');
  const VehicleRegistrationDateLabel = t(
    'vehicle-details.-vehicle-registration-date-label'
  );
  const currentMileageLabel = t('vehicle-details.current-mileage-label');
  const helpText = t('vehicle-details.help-text');
  const deliveryDateCheckboxText = t(
    'vehicle-details.delivery-date-checkbox-text'
  );
  const buttonCancelText = t('vehicle-details.button-cancel-text');
  const vrnEditModalTitle = t('vehicle-details.vrn-edit-modal-title');
  const dealerReferenceLabel = CommonValidation.isNotEmpty(
    externalReferenceLabelText
  )
    ? externalReferenceLabelText
    : t('vehicle-details.dealer-reference-label');

  const optionalFieldText = t('vehicle-details.optional-field-text');
  const salesIdLabel = t('vehicle-details.sales-id-label');
  const policyStartDateLabel = t('vehicle-details.policy-start-date-label');
  const modelTypeLabel = t('vehicle-details.model-type-label');
  const buttonEditText = t('vehicle-details.button-edit-text');
  const makeLabel = t('vehicle-details.make-label');
  const loadingVehicleText = t('vehicle-details.loading-vehicle-text');
  const vinLabel = t('vehicle-details.vin-label');

  return (
    <div>
      <div>
        <div className='form form-left-border'>
          <div className='flex-row items-center'>
            <RegistrationInput
              name='vehicle-registration'
              className='input-text'
              value={vehicleDetails.vrn}
              readOnly
            />
            {showEditVrn === true && (
              <div className='m05-left'>
                <ButtonCommand
                  name='btn-edit'
                  text={buttonEditText}
                  additionalClassNames='small'
                  onClick={onShowModal}
                  //ref={buttonRef}
                />
              </div>
            )}
          </div>
          {vehicleNotFound && vehicleDetails.errorOrigin === 'vrn' && (
            <div className='error-style'>{vehicleDetails.errorMessage}</div>
          )}
          {showVin && (
            <div className='inline-control'>
              <div className='control-label'>{vinLabel}</div>
              <div className='flex-row items-center'>
                <TextInput
                  name='vehicle-vin'
                  className='input-text w201'
                  value={vehicleDetails?.vin}
                  readOnly={isReadOnly}
                />

                {!isReadOnly && (
                  <div className='m05-left'>
                    <ButtonCommand
                      name='btn-edit'
                      text={buttonEditText}
                      additionalClassNames='small'
                      //ref={buttonRef}
                      onClick={onShowModalVin}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
          {vehicleNotFound && vehicleDetails.errorOrigin === 'vin' && (
            <div className='error-style'>{vehicleDetails.errorMessage}</div>
          )}
          <LoadingContainer
            loading={false}
            message={loadingVehicleText}
            colourClass={'primary-color m1-top'}>
            {!vehicleNotFound && (
              <div className='flex-row'>
                <div className='inline-control'>
                  <div className='control-label'>{makeLabel}</div>
                  <Tooltip
                    title={vehicleDetails?.make}
                    enterTouchDelay={0}
                    arrow>
                    <div className='control'>
                      <TextInput
                        name='vehicle-make'
                        className='input-text disabled small'
                        value={vehicleDetails?.make}
                        readOnly
                      />
                    </div>
                  </Tooltip>
                </div>
                <div className='inline-control'>
                  <div className='control-label'>
                    {t('vehicle-details.model-label')}
                  </div>
                  <Tooltip
                    title={vehicleDetails?.model}
                    enterTouchDelay={0}
                    arrow>
                    <div className='control'>
                      <TextInput
                        name='vehicle-model'
                        className='input-text disabled small'
                        value={vehicleDetails?.model}
                        readOnly
                      />
                    </div>
                  </Tooltip>
                </div>
                <div className='inline-control'>
                  <div className='control-label'>
                    {t('vehicle-details.fuel-type-label')}
                  </div>
                  <Tooltip
                    title={vehicleDetails?.fuelType}
                    enterTouchDelay={0}
                    arrow>
                    <div className='control'>
                      <TextInput
                        name='vehicle-fuel-type'
                        className='input-text disabled small'
                        value={vehicleDetails?.fuelType}
                        readOnly
                      />
                    </div>
                  </Tooltip>
                </div>
                {isElectric && (
                  <div className='inline-control'>
                    <div className='control-label'>{brakeHorsepowerLabel}</div>
                    <Tooltip
                      title={brakeHorsepowerLabel}
                      enterTouchDelay={0}
                      arrow>
                      <div className='control'>
                        <TextInput
                          name='vehicle-brake-horsepower'
                          className='input-text disabled small'
                          value={vehicleDetails?.enginePowerBHP?.toString()}
                          readOnly
                        />
                      </div>
                    </Tooltip>
                  </div>
                )}
              </div>
            )}
            {vehicleNotFound ||
              (showModelTrim === true && (
                <div>
                  <div className='control-label'>
                    {modelTypeLabel}{' '}
                    {!showModelSelect && (
                      <ButtonCommand
                        name='btn-show-model-input'
                        text={buttonEditText}
                        additionalClassNames='small'
                        onClick={onEditClick}
                      />
                    )}
                  </div>
                  {showModelSelect ? (
                    <ModelTrimInput
                      vehicleDetails={vehicleDetails}
                      value={vehicleDetails?.modelTrimId.toString()}
                      onSelectedValueChange={onChangeModelTrim}
                      error={errors.modelTrim || ''}
                      token={token}
                    />
                  ) : (
                    <>
                      <Tooltip
                        title={vehicleDetails?.modelType}
                        enterTouchDelay={0}
                        arrow>
                        <div className='control'>
                          <TextInput
                            name='vehicle--model-trimid'
                            className='input-text disabled large'
                            value={vehicleDetails?.modelType}
                            readOnly
                          />
                        </div>
                      </Tooltip>
                    </>
                  )}
                </div>
              ))}
            {!vehicleNotFound && (
              <>
                <div className='control-label'>
                  {VehicleRegistrationDateLabel}
                </div>
                <div className='control'>
                  <TextInput
                    name='vehicle-registration-date'
                    className='input-text disabled small'
                    value={vehicleRegistrationDate}
                    readOnly
                  />
                </div>
              </>
            )}
            {showEditVehicle && (
              <VehicleDetailsManual
                onConfirmManualVehicleModal={onConfirmManualVehicleModal}
                vehicle={vehicle}
                culture={culture}
              />
            )}
          </LoadingContainer>
          {showFieldsOnDataError && (
            <>
              {showMileage && (
                <>
                  <div className='control-label'>{currentMileageLabel}</div>
                  <div className='control'>
                    <NumberInput
                      name='current-mileage'
                      className='input-text small'
                      onChange={(e: React.FormEvent<HTMLInputElement>) =>
                        onChangeMileage(e)
                      }
                      error={errors.currentMileage}
                      value={
                        currentMileage === null ? '' : currentMileage.toString()
                      }
                      decimalPlaces={0}
                      placeholder={t(
                        'vehicle-details.number-input-placeholder',
                        {
                          example: formatNumber(50000, 0, culture)
                        }
                      )}
                      disabled={!isMileageEditable}
                      culture={culture}
                    />
                  </div>
                  {showUpsellAnnualMileage && (
                    <div className='control'>
                      <Checkbox
                        id='upsell-annual-mileage'
                        checked={exceedsUpsellMileageLimit ?? false}
                        text={upsellAnnualMileageText ?? ''}
                        onChange={onUpsellAnnualMileageChange}
                      />
                    </div>
                  )}
                </>
              )}
              {showVehicleDeliveryDate && (
                <>
                  <div className='control-label'>{deliveryDateLabelText}</div>
                  <div className='control'>
                    <div className={getToolbarClassName()}>
                      <DateInput
                        name='vehicle-delivery-date'
                        id='vehicle-delivery-date'
                        className='input-text small'
                        useJson
                        forceTimeReset
                        error={errors.vehicleDeliveryDate}
                        value={vehicleDeliveryDate}
                        onDateChanged={(e: React.FormEvent<HTMLInputElement>) =>
                          onChangeVehicleDeliveryDate(e)
                        }
                        disabled={!isVehicleDeliveryEditable}
                        culture={culture}
                      />
                      {showNeedHelpTooltipDeliveryDate && (
                        <Tooltip
                          title={needHelpTooltipHeader}
                          enterTouchDelay={0}
                          arrow
                          placement='right'>
                          <p>{helpText}</p>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                  {showIsDeliveryDateEstimated && (
                    <div className='control'>
                      <Checkbox
                        id='estimate-delivery-date'
                        checked={deliveryDateEstimated}
                        text={deliveryDateCheckboxText}
                        onChange={onDeliveryDateEstimatedChange}
                        disabled={isReadOnly}
                      />
                    </div>
                  )}
                </>
              )}

              {showGapIntroductionDate && (
                <>
                  <div className='control-label'>
                    {t('vehicle-details.gap-date-label-text')}
                    <span className='info-text m02-left'>
                      {optionalFieldText}
                    </span>
                  </div>
                  <div className='control'>
                    <div className={getGapIntroductionContainerClass()}>
                      <DateInput
                        name='gap-introduction-date'
                        id='gap-introduction-date'
                        className='input-text small'
                        useJson
                        forceTimeReset
                        error={errors.vehicleGapIntroductionDate}
                        value={vehicleGapIntroductionDate}
                        onDateChanged={(e: React.FormEvent<HTMLInputElement>) =>
                          onChangeVehicleGapIntroductionDate(e)
                        }
                        readOnly={isReadOnly}
                        culture={culture}
                      />
                      <Tooltip
                        title={needHelpTooltipGapIntroductionDate}
                        enterTouchDelay={0}
                        arrow
                        placement='right'>
                        <p>{helpText}</p>
                      </Tooltip>
                    </div>
                  </div>
                </>
              )}
              {showVehiclePurchasePrice && (
                <>
                  <div className='control-label'>
                    {vehiclePurchasePriceLabel}
                  </div>
                  <div className='control price-control'>
                    <PriceInput
                      name='vehicle-purchase-price'
                      value={
                        vehiclePurchasePrice
                          ? vehiclePurchasePrice.toString()
                          : ''
                      }
                      onChange={(e: React.FormEvent<HTMLInputElement>) =>
                        onChangeVehiclePurchasePrice(e)
                      }
                      onLeave={(e: React.FormEvent<HTMLInputElement>) =>
                        onChangeVehiclePurchasePrice(e)
                      }
                      currency={currencySymbol}
                      placeholder={t(
                        'vehicle-details.number-input-placeholder',
                        { example: formatNumber(30000, 0, culture) }
                      )}
                      error={errors.vehiclePurchasePrice}
                      disabled={!isPurchasePriceEditable}
                      culture={culture}
                    />
                  </div>
                </>
              )}
              {showPolicyStartDate && (
                <>
                  <div className='control-label'>{policyStartDateLabel}</div>
                  <div className='control'>
                    <div className={getToolbarClassName()}>
                      <DateInput
                        name='policy-start-date'
                        id='policy-start-date'
                        className={policyStartDtClassName}
                        forceTimeReset
                        useJson
                        error={errors.policyStartDate}
                        value={policyStartDateJson}
                        readOnly={!isPolicyStartDateEnabled}
                        onDateChanged={(e: React.FormEvent<HTMLInputElement>) =>
                          onChangePolicyStartDate(e)
                        }
                        minimumDate={getMinDate()}
                        culture={culture}
                      />
                    </div>
                  </div>
                </>
              )}
              {showSalesId && (
                <>
                  <div className='control-label'>
                    {salesIdLabel}
                    {!isSalesIdMandatory && (
                      <span className='info-text m02-left'>
                        {optionalFieldText}
                      </span>
                    )}
                  </div>
                  <div className='control'>
                    <TextInput
                      name='sales-id'
                      placeholder={salesIdLabel}
                      className='input-text small'
                      value={salesId ?? ''}
                      onChange={(e: React.FormEvent<HTMLInputElement>) =>
                        onChangeSalesId(e)
                      }
                      error={errors.salesId}
                    />
                  </div>
                </>
              )}
              {showDealerReference && (
                <>
                  <div className='control-label'>
                    {dealerReferenceLabel}
                    {!(isEmptyVrnAndVin || isSalesIdMandatory) && (
                      <span className='info-text m02-left'>
                        {optionalFieldText}
                      </span>
                    )}
                  </div>
                  <div className='control'>
                    <TextInput
                      name='dealer-reference'
                      placeholder={dealerReferenceLabel}
                      value={dealerReference ?? ''}
                      onChange={(e: React.FormEvent<HTMLInputElement>) =>
                        onChangeDealerReference(e)
                      }
                      error={errors.dealerReference}
                      readOnly={isReadOnly}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
      <Modal isOpen={showModalDialog}>
        <ModalHeader
          toggle={onCloseModal}
          className='modal-header-title cms-modal-header-title'>
          {vrnEditModalTitle}
          <span className='close' aria-hidden='true' onClick={onCloseModal}>
            ×
          </span>
        </ModalHeader>
        <ModalBody>
          <div className='m2-left'>
            <RegistrationInput
              name='vehicle-registration-number'
              className='input-text'
              value={updatedVrn}
              error={vrnError}
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                onChangeVehicleReg(e)
              }
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <div className='flex-row button-row'>
            <ButtonCommand
              name='btn-close'
              text={buttonCancelText}
              onClick={onCloseModal}
            />
            <ButtonConfirm
              name='btn-confirm'
              text={buttonConfirmText}
              additionalClassNames='m05-left'
              onClick={onConfirmModal}
            />
          </div>
        </ModalFooter>
      </Modal>

      <Modal isOpen={showModalDialogVin}>
        <ModalHeader
          toggle={onCloseModalVin}
          className='modal-header-title cms-modal-header-title'>
          {t('vehicle-details.vin-edit-modal-title')}
          <span className='close' aria-hidden='true' onClick={onCloseModalVin}>
            ×
          </span>
        </ModalHeader>
        <ModalBody>
          <div className='m2-left'>
            <TextInput
              label={t('vehicle-details.vin-label')}
              placeholder={t('vehicle-details.vin-label')}
              name='VIN'
              className='input-text w201'
              value={updatedVin}
              error={vinError}
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                onChangeVin(e)
              }
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <div className='flex-row button-row'>
            <ButtonCommand
              name='btn-close'
              text={buttonCancelText}
              onClick={onCloseModalVin}
            />
            <ButtonConfirm
              name='btn-confirm'
              text={buttonConfirmText}
              additionalClassNames='m05-left'
              onClick={onConfirmModalVin}
            />
          </div>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default VehiclePage;
